import {Component, Input, OnDestroy, OnInit, SimpleChanges} from "@angular/core";
import {BehaviorSubject, combineLatest, filter, Subscription} from "rxjs";
import {
  Aangifte,
  AangifteBerekening,
  AangifteRapport, BokkzAlgDataService,
  BokkzIbDataService, Box1Berekening,
  IncomeTaxEntity, OpbouwInkomenBox1, OpbouwPU, SpecificatiePU
} from "@snelstart-ventures/fiscaal/domain-logic";
import {AbstractControl, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {Event, NavigationEnd, Router} from "@angular/router";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-box1-belastbare-periodieke-uitkeringen-en-verstrekkingen',
    templateUrl: './box1-belastbare-periodieke-uitkeringen-en-verstrekkingen.component.html',
    styleUrls: ['./box1-belastbare-periodieke-uitkeringen-en-verstrekkingen.component.scss',],
})

export class Box1BelastbarePeriodiekeUitkeringenEnVerstrekkingenComponent implements OnInit, OnDestroy {

    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isDefAangifteSubscription: Subscription | undefined;
    private isSaving = false;

    aangifte = {} as Aangifte;
    public incomeTaxEntity = {} as IncomeTaxEntity;

    @Input() partner = new BehaviorSubject<boolean>(false);
    public clientId!: string;

    public partnerBoolean = false;

    @Input() aangifteRapport = {} as AangifteRapport;
    @Input() partnerRapport = {} as AangifteRapport;
    @Input() berekening = {} as AangifteBerekening;
    @Input() partnerBerekening = {} as AangifteBerekening;

    public isDefAangifte = {} as boolean;

    specPUFormGroup = this.fb.group<AangifteRapport>({});
    specPU = {} as AangifteRapport;
    berekeningBox1FormGroup = this.fb.group<AangifteBerekening>({});

    constructor(public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService,
                private router: Router,
                private fb: FormBuilder) {
        this.specPUFormGroup = this.fb.group<AangifteRapport>(({
            specificatiePU: this.fb.group<SpecificatiePU>(({
                opbouw: this.fb.group<OpbouwPU>(({
                    berekendeUitkomst: null as unknown as number,
                    ontvangenAlimentatie: null as unknown as number,
                    kostenAlimentatie: null as unknown as number,
                    ontvangenPeriodiekeUitkeringen: null as unknown as number,
                    kostenPeriodiekeUitkeringen: null as unknown as number,
                    saldoAlimentatie: null as unknown as number,
                    saldoPerUitkeringen: null as unknown as number,
                } as OpbouwPU))
            } as SpecificatiePU))
        } as AangifteRapport))
        this.berekeningBox1FormGroup = this.fb.group<AangifteBerekening>(({
            box1: this.fb.group<Box1Berekening>(({
                opbouwInkomen: this.fb.group<OpbouwInkomenBox1>(({
                    periodiekeUitkeringen: null as unknown as number,
                } as OpbouwInkomenBox1)),
            } as Box1Berekening)),
        } as AangifteBerekening))
    }

    public ngOnInit(): void {
        this.isDefAangifteSubscription = this.dataService.isDefAangifte.subscribe((isDefAangifte) => {
            this.isDefAangifte = isDefAangifte;
        });
        combineLatest([
            this.dataService.incomeTaxEntity,
            this.dataService.aangifteBerekening,
            this.dataService.partnerAangifteBerekening,
            this.dataService.partnerAangifteRapport,
            this.dataService.aangifteRapport,
            this.partner
        ]).subscribe(
            ([incomeTaxEntity, berekening, partnerBerekening, partnerRapport, aangifteRapport, partner]) => {
                this.specPUFormGroup.reset();
                this.berekeningBox1FormGroup.reset();
                this.berekening = berekening;
                this.partnerBerekening = partnerBerekening;
                this.aangifteRapport = aangifteRapport;
                this.partnerRapport = partnerRapport;
                this.partnerBoolean = partner;
                if (this.partnerBoolean) {
                    this.specPU = this.partnerRapport;
                    if (this.berekening.partnerBerekening) {
                        this.berekeningBox1FormGroup.reset();
                        this.berekeningBox1FormGroup.patchValue(this.berekening.partnerBerekening);
                    }
                    if (incomeTaxEntity.aangifte) {
                        const aangifte = incomeTaxEntity.aangifte as Aangifte;
                        this.aangifte = aangifte;
                        this.updateForm();
                        this.disableFormGroup(this.specPUFormGroup);
                        this.disableFormGroup(this.berekeningBox1FormGroup);
                    }
                } else {
                    this.specPU = this.aangifteRapport;
                    if (this.berekening) {
                        this.berekeningBox1FormGroup.reset();
                        this.berekeningBox1FormGroup.patchValue(this.berekening);
                    }
                    if (incomeTaxEntity.aangifte) {
                        const aangifte = incomeTaxEntity.aangifte as Aangifte;
                        this.aangifte = aangifte;
                        this.updateForm();
                        this.disableFormGroup(this.specPUFormGroup);
                        this.disableFormGroup(this.berekeningBox1FormGroup);
                    }
                }
            }
        );
        this.routerSubscription = this.router.events
            .pipe(
                filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe(); // Unsubscribe from income tax entity
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
        if (this.isDefAangifteSubscription) {
            this.isDefAangifteSubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId,false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.specPUFormGroup.dirty || this.berekeningBox1FormGroup.dirty;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['aangifteRapport'] || changes['partnerRapport'] || changes['berekening'] || changes['partnerBerekening']) {
            this.updateForm();
        }
    }

    private updateForm() {
        if (this.partnerBoolean) {
            this.specPU = this.partnerRapport;
            this.specPUFormGroup.patchValue(this.partnerRapport);
            this.berekeningBox1FormGroup.patchValue(this.partnerBerekening)
            this.updateSaldoAlimentatie()
            this.updateSaldoPerUitkeringen()
        } else {
            this.specPU = this.aangifteRapport;
            this.specPUFormGroup.patchValue(this.aangifteRapport);
            this.berekeningBox1FormGroup.patchValue(this.berekening)
            this.updateSaldoAlimentatie()
            this.updateSaldoPerUitkeringen()
        }
    }

    private updateSaldoAlimentatie() {
        const inkomsten = this.specPUFormGroup.get('specificatiePU.opbouw.ontvangenAlimentatie')?.value || 0;
        const kosten = this.specPUFormGroup.get('specificatiePU.opbouw.kostenAlimentatie')?.value || 0;

        const saldo = inkomsten - kosten;
        this.specPUFormGroup.get('specificatiePU.opbouw.saldoAlimentatie')?.setValue(saldo);
    }

    private updateSaldoPerUitkeringen() {
        const inkomsten = this.specPUFormGroup.get('specificatiePU.opbouw.ontvangenPeriodiekeUitkeringen')?.value || 0;
        const kosten = this.specPUFormGroup.get('specificatiePU.opbouw.kostenPeriodiekeUitkeringen')?.value || 0;

        const saldo = inkomsten - kosten;
        this.specPUFormGroup.get('specificatiePU.opbouw.saldoPerUitkeringen')?.setValue(saldo);
    }

    disableFormGroup(formGroup: FormGroup | AbstractControl): void {
        if (formGroup instanceof FormGroup) {
            Object.keys(formGroup.controls).forEach((key) => {
                const control = formGroup.get(key);
                if (control instanceof FormControl) {
                    control.disable();
                } else if (control instanceof FormGroup) {
                    this.disableFormGroup(control);
                }
            });
        }
    }


}
